/**
 * Google Analytics 4 through Firebase (docs/google-analytics.md).
 *
 * Off unless the build has a measurement ID (Terraform outputs it once Analytics is
 * linked to the Firebase project) and the person allowed it on this browser. Until
 * then nothing is loaded from Google: the Analytics SDK is imported on consent.
 *
 * Never send personal data. Page views carry the route pattern, not the URL, and
 * events carry no parameters at all.
 */
import type { Analytics } from 'firebase/analytics';
import { useSyncExternalStore } from 'react';
import { analyticsConfigured } from './config';
import { app } from './firebase';
import { routePattern } from './lib/routes';

export type AnalyticsChoice = 'granted' | 'denied';

/** Events the app sends; keep them parameter-free. */
export type AppEvent = 'login' | 'client_created' | 'client_removed' | 'vehicle_added' | 'staff_member_added';

type Sdk = typeof import('firebase/analytics');

const STORAGE_KEY = 'analytics-consent';
let sdk: Sdk | null = null;
let analytics: Analytics | null = null;
let collecting = false;
let currentRoute = '';
const listeners = new Set<() => void>();

function readChoice(): AnalyticsChoice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

let choice = analyticsConfigured ? readChoice() : null;

function sendPageView() {
  if (sdk && analytics && collecting && currentRoute) {
    sdk.logEvent(analytics, 'page_view', {
      page_location: `${location.origin}${currentRoute}`,
      page_path: currentRoute,
      page_title: currentRoute,
    });
  }
}

async function start() {
  if (collecting) return;
  sdk ??= await import('firebase/analytics');
  if (!(await sdk.isSupported())) return;
  if (!analytics) {
    // Measurement only: no advertising features, no Google signals.
    sdk.setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    analytics = sdk.initializeAnalytics(app, {
      config: { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false },
    });
  } else {
    sdk.setConsent({ analytics_storage: 'granted' });
    sdk.setAnalyticsCollectionEnabled(analytics, true);
  }
  collecting = true;
  sendPageView();
}

function stop() {
  if (sdk && analytics) {
    sdk.setConsent({ analytics_storage: 'denied' });
    sdk.setAnalyticsCollectionEnabled(analytics, false);
  }
  collecting = false;
  // Remove the Analytics cookies (_ga, _ga_<id>) set while it was allowed.
  for (const name of document.cookie.split(';').map((c) => c.split('=')[0]!.trim())) {
    if (name.startsWith('_ga')) {
      for (const domain of ['', `; domain=${location.hostname}`, `; domain=.${location.hostname}`]) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      }
    }
  }
}

/** Called once at start-up: resumes collection if this browser allowed it before. */
export function initAnalytics() {
  if (choice === 'granted') void start();
}

export function setAnalyticsChoice(next: AnalyticsChoice) {
  if (!analyticsConfigured) return;
  choice = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private windows: the choice lasts for this page load only.
  }
  if (next === 'granted') void start();
  else stop();
  listeners.forEach((l) => l());
}

/** The current choice on this browser; null when the person has not been asked yet. */
export function useAnalyticsChoice(): AnalyticsChoice | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => choice,
  );
}

export function trackPage(pathname: string) {
  const route = routePattern(pathname);
  if (route === currentRoute) return;
  currentRoute = route;
  sendPageView();
}

export function trackEvent(name: AppEvent) {
  // logEvent's overloads type each standard event separately; ours carry no parameters.
  if (sdk && analytics && collecting) (sdk.logEvent as (a: Analytics, n: string) => void)(analytics, name);
}
